import { withErrorBoundary } from "@sentry/nextjs";
import cn from "classnames";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { PlusIcon } from "icons";
import { useControlled } from "./useControlled";

type Props = {
  button?: any;
  buttonLink?: any;
  className?: string;
  children: ReactNode;
  description?: string;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: (expanded: boolean) => void; // eslint-disable-line no-unused-vars
  title?: string;
};

const Accordion: FC<Props> = withErrorBoundary(
  ({
    className = "",
    children,
    defaultExpanded = undefined,
    expanded: expandedProp = undefined,
    onChange = () => {},
    title = "",
  }) => {
    const [contentHeight, setContentHeight] = useState(0);

    const [expanded, setExpandedState] = useControlled({
      controlled: expandedProp,
      defaultValue: defaultExpanded,
    });

    const contentRef = useRef<HTMLDivElement>(null);

    const handleToggle = () => {
      setExpandedState(!expanded);

      if (onChange) {
        onChange(!expanded);
      }
    };

    useEffect(() => {
      if (expanded && contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight);
      } else {
        setContentHeight(0);
      }
    }, [expanded]);

    return (
      <StyledAccordion $contentWrapperHeight={contentHeight} className={className}>
        <div className="Accordion__title" onClick={handleToggle}>
          <h4 className="Color_black Font_24_32_600 sm:Font_18_26_700 Color_primary">{title}</h4>
          <PlusIcon height={24} width={24} className={cn("Accordion__icon", { Accordion__icon_rotated: expanded })} />
        </div>
        <div className="Accordion__content">
          <div className="Accordion__contentWrapper" ref={contentRef}>
            <div className="Accordion__text Font_24_32_450 sm:Font_18_26_450 Color_secondary">{children}</div>
          </div>
        </div>
      </StyledAccordion>
    );
  },
  {}
);

const StyledAccordion = styled.div<{ $contentWrapperHeight: number }>`
  padding-bottom: 32px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.fog["30"]};

  &:last-of-type {
    .Accordion__content {
      border-bottom: unset;
    }
  }

  .Accordion__title {
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    padding: 32px 0 0;
  }

  /* stylelint-disable-next-line no-descending-specificity */
  .Accordion__content {
    overflow: hidden;
  }

  .Accordion__contentWrapper {
    height: ${(props) => props.$contentWrapperHeight}px;
    transition: height 0.175s ease;
    max-width: 90%;

    a {
      text-decoration: underline;

      &:hover {
        color: ${({ theme }) => theme.colors.fog["80"]};
      }

      &.active {
        opacity: 0.7;
      }
    }
  }

  .Accordion__text {
    max-width: 90%;
    padding-top: 16px;

    /* stylelint-disable-next-line no-descending-specificity */
    a {
      text-decoration: underline;

      &:hover {
        color: ${({ theme }) => theme.colors.fog["80"]};
      }

      &.active {
        opacity: 0.7;
      }
    }
  }

  .Accordion__icon {
    fill: ${({ theme }) => theme.colors.fog["80"]};
    margin-left: 80px;
    flex-shrink: 0;
    transition: transform 0.3s;
    margin-top: 4px;

    &_rotated {
      transform: rotate(-45deg);
    }
  }

  .Accordion__button {
    width: fit-content;
    margin-bottom: 32px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Accordion__title {
      /* padding: 16px 6px 16px 0; */
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    padding-bottom: 24px;

    .Accordion__title {
      padding: 24px 0 0;
    }

    .Accordion__icon {
      margin-left: 24px;
    }
  }
`;

export { Accordion };
